/**
 * ===== KONVA CACHE MANAGER - GESTION DU CACHE KONVA =====
 * Gestionnaire avanc\u00e9 du cache Konva avec hit rate et optimisations;
 */
import Konva from 'konva';
import { logger } from './ProfessionalLogger';
export interface CacheStats {
  totalCacheSize: number;
  hitRate: number;
  missRate: number;
  cacheEntries: number;
  memoryUsage: number;
  lastCleanup: string;
  performanceScore: number;
}
export interface CacheConfig {
  maxCacheSize: number;
  maxEntries: number;
  cleanupThreshold: number;
  enableHitRateTracking: boolean;
  enableMemoryOptimization: boolean;
  cacheTimeout: number;
}
export interface CacheEntry {
  id: string;
  node: Konva.Node;
  cacheData: string;
  size: number;
  lastAccessed: number;
  accessCount: number;
  priority: number;
  isDirty: boolean;
}

export class KonvaCacheManager {
  private static instance: KonvaCacheManager;
  private cache: Map<string, CacheEntry> = new Map();
  private config: CacheConfig;
  private stats: CacheStats;
  private cleanupTimer: NodeJS.Timeout | null = null;
  private isInitialized: boolean = false;
  private constructor() {
    this.config = {
      maxCacheSize: 50 * 1024 * 1024, // 50MB;
      maxEntries: 1000,
      cleanupThreshold: 0.8,
      enableHitRateTracking: true,
      enableMemoryOptimization: true,
      cacheTimeout: 300000, // 5 minutes;
    };
    this.stats = {
      totalCacheSize: 0,
      hitRate: 0,
      missRate: 0,
      cacheEntries: 0,
      memoryUsage: 0,
      lastCleanup: new Date().toISOString(),
      performanceScore: 0,
    };
  }
  public static getInstance(): KonvaCacheManager {
    if (!KonvaCacheManager.instance) {
      KonvaCacheManager.instance = new KonvaCacheManager();
    }
    return KonvaCacheManager.instance;
  }
  /**
   * Retourne la configuration courante (utilisé par useKonvaCacheCleanup).
   */
  public getConfig(): CacheConfig {
    return { ...this.config };
  }
  /**
   * Initialise le gestionnaire de cache;
   */
  public initialize(config: Partial<CacheConfig> = {}): void {
    if (this.isInitialized) {
      logger.debug('KonvaCacheManager d\u00e9j\u00e0 initialis\u00e9');
      return;
    }
    this.config = { ...this.config, ...config };
    this.startCleanupTimer();
    this.isInitialized = true;
    logger.info('KonvaCacheManager initialis\u00e9', {
      maxCacheSize: this.config.maxCacheSize,
      maxEntries: this.config.maxEntries,
    });
  }
  /**
   * Met en cache un n\u0153ud Konva;
   */
  public cacheNode(node: Konva.Node, priority: number = 1): string {
    try {
      const id = this.generateCacheId(node);
      // Vérifier si le nœud est déjà en cache;
      if (this.cache.has(id)) {
        this.updateCacheEntry(id);
        return id;
      }
      // Vérifier les limites de cache;
      if (this.shouldCleanup()) {
        this.performCleanup();
      }
      // Créer l'entrée de cache;
      const cacheData = this.serializeNode(node);
      const size = this.calculateCacheSize(cacheData);
      const entry: CacheEntry = {
        id,
        node: node.clone(),
        cacheData,
        size,
        lastAccessed: Date.now(),
        accessCount: 1,
        priority,
        isDirty: false,
      };
      this.cache.set(id, entry);
      this.updateStats();
      logger.debug('Nœud mis en cache', { id, size, priority });
      return id;
    } catch (error) {
      logger.error('Erreur lors de la mise en cache du nœud:', error);
      return '';
    }
  }
  /**
   * R\u00e9cup\u00e8re un n\u0153ud depuis le cache;
   */
  public getCachedNode(id: string): Konva.Node | null {
    try {
      const entry = this.cache.get(id);
      if (!entry) {
        this.stats.missRate++;
        return null;
      }
      // Mettre à jour les statistiques;
      entry.lastAccessed = Date.now();
      entry.accessCount++;
      this.stats.hitRate++;
      // Vérifier si le cache est expiré;
      if (this.isCacheExpired(entry)) {
        this.cache.delete(id);
        this.stats.missRate++;
        return null;
      }
      // Cloner le nœud pour éviter les modifications;
      const clonedNode = entry.node.clone();
      logger.debug('Nœud récupéré du cache', { id, accessCount: entry.accessCount });
      return clonedNode;
    } catch (error) {
      logger.error('Erreur lors de la récupération du nœud depuis le cache:', error);
      return null;
    }
  }
  /**
   * Invalide le cache d'un n\u0153ud;
   */
  public invalidateCache(id: string): boolean {
    try {
      const entry = this.cache.get(id);
      if (entry) {
        entry.isDirty = true;
        this.cache.delete(id);
        this.updateStats();
        logger.debug('Cache invalidé', { id });
        return true;
      }
      return false;
    } catch (error) {
      logger.error("Erreur lors de l'invalidation du cache:", error);
      return false;
    }
  }
  /**
   * Invalide le cache de tous les n\u0153uds d'un type;
   */
  public invalidateCacheByType(nodeType: string): number {
    let invalidatedCount = 0;
    try {
      for (const [id, entry] of this.cache.entries()) {
        if (entry.node.getClassName() === nodeType) {
          entry.isDirty = true;
          this.cache.delete(id);
          invalidatedCount++;
        }
      }
      this.updateStats();
      logger.info('Cache invalidé par type', { nodeType, count: invalidatedCount });
      return invalidatedCount;
    } catch (error) {
      logger.error("Erreur lors de l'invalidation du cache par type:", error);
      return 0;
    }
  }
  /**
   * Nettoie le cache;
   */
  public performCleanup(): void {
    try {
      const entries = Array.from(this.cache.entries());
      const now = Date.now();
      // Trier par priorité et dernière utilisation;
      entries.sort((a, b) => {
        const scoreA = this.calculateCacheScore(a[1], now);
        const scoreB = this.calculateCacheScore(b[1], now);
        return scoreA - scoreB;
      });
      // Supprimer les entrées les moins prioritaires;
      const entriesToRemove = Math.floor(entries.length * this.config.cleanupThreshold);
      for (let i = 0; i < entriesToRemove; i++) {
        const [id, entry] = entries[i];
        this.cache.delete(id);
        entry.node.destroy();
      }
      this.stats.lastCleanup = new Date().toISOString();
      this.updateStats();
      logger.info('Nettoyage du cache effectué', {
        entriesRemoved: entriesToRemove,
        remainingEntries: this.cache.size,
      });
    } catch (error) {
      logger.error('Erreur lors du nettoyage du cache:', error);
    }
  }
  /**
   * Vide compl\u00e8tement le cache;
   */
  public clearCache(): void {
    try {
      // Détruire tous les nœuds;
      for (const entry of this.cache.values()) {
        entry.node.destroy();
      }
      this.cache.clear();
      this.updateStats();
      logger.info('Cache vidé complètement');
    } catch (error) {
      logger.error('Erreur lors du vidage du cache:', error);
    }
  }
  /**
   * Obtient les statistiques du cache;
   */
  public getCacheStats(): CacheStats {
    this.updateStats();
    return { ...this.stats };
  }
  /**
   * Optimise le cache pour les performances;
   */
  public optimizeCache(): void {
    try {
      // Supprimer les entrées expirées;
      const _now = Date.now();
      for (const [id, entry] of this.cache.entries()) {
        if (this.isCacheExpired(entry)) {
          this.cache.delete(id);
          entry.node.destroy();
        }
      }
      // Optimiser la mémoire;
      if (this.config.enableMemoryOptimization) {
        this.optimizeMemoryUsage();
      }
      this.updateStats();
      logger.info('Cache optimisé', { entries: this.cache.size });
    } catch (error) {
      logger.error("Erreur lors de l'optimisation du cache:", error);
    }
  }
  /**
   * Configure le gestionnaire de cache;
   */
  public configure(config: Partial<CacheConfig>): void {
    this.config = { ...this.config, ...config };
    // Red\u00e9marrer le timer de nettoyage;
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
    }
    this.startCleanupTimer();
    logger.info('Configuration du cache mise \u00e0 jour', config);
  }
  /**
   * G\u00e9n\u00e8re un ID unique pour le cache;
   */
  private generateCacheId(node: Konva.Node): string {
    const nodeType = node.getClassName();
    const nodeId = node.id() || 'anonymous';
    const nodeHash = this.hashNode(node);
    return `${nodeType}-${nodeId}-${nodeHash}`;
  }
  /**
   * Calcule un hash pour un n\u0153ud;
   */
  private hashNode(node: Konva.Node): string {
    const attrs = node.getAttrs();
    const attrsString = JSON.stringify(attrs);
    return this.simpleHash(attrsString);
  }
  /**
   * Hash simple pour les n\u0153uds;
   */
  private simpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convertir en 32-bit;
    }
    return Math.abs(hash).toString(36);
  }
  /**
   * S\u00e9rialise un n\u0153ud Konva;
   */
  private serializeNode(node: Konva.Node): string {
    try {
      return JSON.stringify(node.toObject());
    } catch (error) {
      logger.error('Erreur lors de la sérialisation du nœud:', error);
      return '';
    }
  }

  /**
   * Calcule la taille du cache;
   */
  private calculateCacheSize(cacheData: string): number {
    return new Blob([cacheData]).size;
  }
  /**
   * Met \u00e0 jour une entr\u00e9e de cache;
   */
  private updateCacheEntry(id: string): void {
    const entry = this.cache.get(id);
    if (entry) {
      entry.lastAccessed = Date.now();
      entry.accessCount++;
    }
  }
  /**
   * V\u00e9rifie si le cache doit \u00eatre nettoy\u00e9;
   */
  private shouldCleanup(): boolean {
    return (
      this.cache.size >= this.config.maxEntries ||
      this.stats.totalCacheSize >= this.config.maxCacheSize * this.config.cleanupThreshold
    );
  }
  /**
   * V\u00e9rifie si une entr\u00e9e de cache est expir\u00e9e;
   */
  private isCacheExpired(entry: CacheEntry): boolean {
    const now = Date.now();
    return now - entry.lastAccessed > this.config.cacheTimeout;
  }
  /**
   * Calcule le score de cache d'une entr\u00e9e;
   */
  private calculateCacheScore(entry: CacheEntry, now: number): number {
    const age = now - entry.lastAccessed;
    const frequency = entry.accessCount;
    const priority = entry.priority;
    // Score bas\u00e9 sur l'\u00e2ge, la fr\u00e9quence et la priorit\u00e9;
    return age / 1000 - frequency * 10 - priority * 100;
  }
  /**
   * Optimise l'utilisation de la m\u00e9moire;
   */
  private optimizeMemoryUsage(): void {
    // Forcer le garbage collection si disponible;
    if (typeof window !== 'undefined' && window.gc) {
      window.gc();
    }
  }
  /**
   * Met \u00e0 jour les statistiques;
   */
  private updateStats(): void {
    this.stats.cacheEntries = this.cache.size;
    this.stats.totalCacheSize = Array.from(this.cache.values()).reduce(
      (total, entry) => total + entry.size,
      0
    );
    const totalRequests = this.stats.hitRate + this.stats.missRate;
    if (totalRequests > 0) {
      this.stats.hitRate = (this.stats.hitRate / totalRequests) * 100;
      this.stats.missRate = (this.stats.missRate / totalRequests) * 100;
    }
    // Calculer le score de performance;
    this.stats.performanceScore = this.calculatePerformanceScore();
    // Estimer l'utilisation m\u00e9moire;
    this.stats.memoryUsage = this.estimateMemoryUsage();
  }
  /**
   * Calcule le score de performance;
   */
  private calculatePerformanceScore(): number {
    const hitRateScore = this.stats.hitRate;
    const sizeScore = Math.max(
      0,
      100 - (this.stats.totalCacheSize / this.config.maxCacheSize) * 100
    );
    const entriesScore = Math.max(
      0,
      100 - (this.stats.cacheEntries / this.config.maxEntries) * 100
    );
    return (hitRateScore + sizeScore + entriesScore) / 3;
  }
  /**
   * Estime l'utilisation m\u00e9moire;
   */
  private estimateMemoryUsage(): number {
    return this.stats.totalCacheSize * 1.5; // Estimation avec overhead;
  }
  /**
   * D\u00e9marre le timer de nettoyage;
   * NEUTRALISÉ: setInterval remplacé par hook React useKonvaCacheCleanup
   */
  private startCleanupTimer(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = null;
    }
    // NEUTRALISÉ: setInterval supprimé - géré par hook React
    // Le hook useKonvaCacheCleanup remplace cette fonctionnalité
  }
  /**
   * Nettoie les ressources;
   */
  public destroy(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = null;
    }
    this.clearCache();
    this.isInitialized = false;
    logger.info('KonvaCacheManager d\u00e9truit');
  }
}
